import { useState } from 'react'
import { Check, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useMyMatters } from '@/shared/hooks/useMyMatters'
import { getRecentMatters, searchMatters } from '@/shared/utils/matterSearch'
import { matterDisplayLabel, matterLookupText, matterSearchFields } from '@/shared/utils/matterLabels'
import { ComboBox } from '@/shared/components/ComboBox/ComboBox'
import { renderMatterItem } from '@/features/attorney/components/v2/pickerItems'
import { Button } from '@/shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '@/shared/components/ui/dialog'

export function MyMattersSettings() {
	const {
		matters,
		caseListIds,
		linkedMatters,
		unknownLinkedIds,
		linkedCount,
		isLoading,
		addMatter,
		removeMatter,
	} = useMyMatters()

	const [adding, setAdding] = useState(false)
	const [removing, setRemoving] = useState(false)
	const [pendingRemoval, setPendingRemoval] = useState<{
		matterId: string
		label: string
	} | null>(null)

	const handleAdd = async (matterId: string) => {
		setAdding(true)
		const result = await addMatter(matterId)
		if (result.already) {
			toast.info('Already on your list')
		} else if (!result.ok) {
			toast.error(result.error ?? 'Failed to add matter')
		} else {
			toast.success('Matter added')
		}
		setAdding(false)
	}

	const confirmRemove = async () => {
		if (!pendingRemoval) return
		setRemoving(true)
		const result = await removeMatter(pendingRemoval.matterId)
		if (!result.ok) {
			toast.error(result.error ?? 'Failed to remove matter')
		} else {
			toast.success('Matter removed')
			setPendingRemoval(null)
		}
		setRemoving(false)
	}

	return (
		<>
			<h2 className="text-lg text-muted-foreground mb-1">My Matters</h2>
			<p className="text-sm text-muted-foreground mb-5 max-w-[480px] leading-relaxed">
				Matters on your list help Hourglass predict the right matter when you
				track time.
			</p>

			<div className="mb-6">
				<label className="text-xs font-medium text-muted-foreground mb-1.5 block">
					Add a matter
				</label>
				<ComboBox
					items={matters}
					value={null}
					onChange={handleAdd}
					fields={matterSearchFields}
					getDefaultItems={getRecentMatters}
					searchItems={searchMatters}
					getKey={(m) => m.matter_id}
					getDisplayLabel={matterDisplayLabel}
					renderItem={(m) => (
						<div className="flex items-center gap-2 min-w-0">
							<div className="min-w-0 flex-1">{renderMatterItem(m)}</div>
							{caseListIds.has(m.matter_id) && (
								<span className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground">
									<Check className="h-3.5 w-3.5" />
									Linked
								</span>
							)}
						</div>
					)}
					placeholder="Search matters to add..."
					disabled={adding || isLoading}
					clearable
				/>
			</div>

			<div className="flex items-center justify-between mb-2">
				<span className="text-xs font-medium text-muted-foreground tabular-nums">
					{linkedCount} matter{linkedCount !== 1 && 's'}
				</span>
			</div>

			{isLoading ? (
				<div className="flex flex-col gap-2 animate-pulse">
					<div className="h-14 rounded-md bg-muted/50" />
					<div className="h-14 rounded-md bg-muted/50" />
				</div>
			) : linkedCount === 0 ? (
				<div className="rounded-md border border-dashed border-border/50 bg-muted/20 px-4 py-8 text-center">
					<p className="text-sm text-muted-foreground">
						No matters on your list yet. Add one above to start logging time
						against it.
					</p>
				</div>
			) : (
				<div className="rounded-md border border-border/50 divide-y divide-border/50 overflow-hidden">
					{linkedMatters.map((m) => (
						<div key={m.matter_id} className="flex items-center gap-3 px-4 py-3">
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium font-mono truncate">
									{matterLookupText(m)}
								</p>
								<p className="text-sm text-muted-foreground truncate">
									{m.description.trim()}
								</p>
								<p className="text-xs text-muted-foreground/70 truncate">
									{m.client.trim()}
								</p>
							</div>
							<button
								type="button"
								onClick={() =>
									setPendingRemoval({
										matterId: m.matter_id,
										label: matterLookupText(m),
									})
								}
								className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
								aria-label="Remove matter"
							>
								<Trash2 className="h-4 w-4" />
							</button>
						</div>
					))}
					{unknownLinkedIds.map((id) => (
						<div key={id} className="flex items-center gap-3 px-4 py-3">
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium font-mono truncate">{id}</p>
								<p className="text-xs text-muted-foreground/70">
									Details unavailable
								</p>
							</div>
							<button
								type="button"
								onClick={() => setPendingRemoval({ matterId: id, label: id })}
								className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
								aria-label="Remove matter"
							>
								<Trash2 className="h-4 w-4" />
							</button>
						</div>
					))}
				</div>
			)}

			<Dialog
				open={pendingRemoval !== null}
				onOpenChange={(open) => !open && setPendingRemoval(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Remove matter?</DialogTitle>
						<DialogDescription>{pendingRemoval?.label}</DialogDescription>
					</DialogHeader>
					<p className="text-sm text-muted-foreground">
						Hourglass will be less likely to predict this matter when you track
						time.
					</p>
					<DialogFooter>
						<Button
							variant="ghost"
							onClick={() => setPendingRemoval(null)}
							disabled={removing}
						>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={confirmRemove}
							loading={removing}
						>
							Remove
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
